import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Award, MapPin, Calendar, Users, Star, CheckCircle, Truck, AlertTriangle, MessageSquare } from 'lucide-react';
import { getVendorDisplayName } from '@/utils/vendorHelpers';
import { Review } from '@/lib/types/review';
import { ReviewCard } from '@/components/reviews/ReviewCard';

interface GalleryImage {
  url: string;
  uploaded_at: string;
  storage_key: string;
}

interface VendorSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: any;
  certifications: any[];
  capabilities: any[];
  reviewAggregate: any;
  reviews?: Review[];
}

export function VendorSummaryModal({ 
  isOpen, 
  onClose, 
  profile, 
  certifications, 
  capabilities, 
  reviewAggregate,
  reviews
}: VendorSummaryModalProps) {
  const [showAllReviews, setShowAllReviews] = useState(false);
  const displayedReviews = showAllReviews ? reviews : reviews?.slice(0, 3);
  const getVerificationBadge = (profile: any) => {
    if (!profile) return null;
    
    if (profile.is_verified) {
      return (
        <Badge variant="default" className="flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Verified
        </Badge>
      );
    }
    
    return (
      <Badge variant="secondary" className="flex items-center gap-1">
        Unverified
      </Badge>
    );
  };

  const formatScore = (score: number | null) => {
    if (!score) return 'N/A';
    return score.toFixed(1);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Vendor Summary
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Company Header */}
          <div className="text-center space-y-3">
            <h2 className="text-3xl font-bold">{getVendorDisplayName(profile)}</h2>
            {profile?.business_description && (
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {profile.business_description}
              </p>
            )}
            <div className="flex items-center justify-center gap-2">
              {getVerificationBadge(profile)}
              <Badge variant="outline">{profile?.verification_level || 'Basic'}</Badge>
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <Users className="h-8 w-8 mx-auto mb-2 text-primary" />
                <div className="text-2xl font-bold text-primary">
                  {profile?.employee_count_range || 'Not Set'}
                </div>
                <div className="text-sm text-muted-foreground">Employees</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <Calendar className="h-8 w-8 mx-auto mb-2 text-primary" />
                <div className="text-2xl font-bold text-primary">
                  {profile?.founded_year || 'Not Set'}
                </div>
                <div className="text-sm text-muted-foreground">Founded</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-2 text-primary" />
                <div className="text-2xl font-bold text-primary">
                  {profile?.state || 'Not Set'}
                </div>
                <div className="text-sm text-muted-foreground">State</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-primary" />
                <div className="text-2xl font-bold text-primary">
                  {profile?.total_jobs_completed || 0}
                </div>
                <div className="text-sm text-muted-foreground">Jobs Completed</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <Star className="h-8 w-8 mx-auto mb-2 text-primary" />
                <div className="text-2xl font-bold text-primary">
                  {formatScore(reviewAggregate?.avg_overall_rating)}
                </div>
                <div className="text-sm text-muted-foreground">Overall Rating</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <Award className="h-8 w-8 mx-auto mb-2 text-primary" />
                <div className="text-2xl font-bold text-primary">
                  {certifications?.length || 0}
                </div>
                <div className="text-sm text-muted-foreground">Certifications</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <Truck className="h-8 w-8 mx-auto mb-2 text-primary" />
                <div className="text-2xl font-bold text-primary">
                  {profile?.on_time_delivery_rate != null ? `${profile.on_time_delivery_rate}%` : 'N/A'}
                </div>
                <div className="text-sm text-muted-foreground">On-Time Delivery</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <AlertTriangle className={`h-8 w-8 mx-auto mb-2 ${(profile?.dispute_count || 0) === 0 ? 'text-primary' : (profile?.dispute_count || 0) <= 2 ? 'text-yellow-500' : 'text-destructive'}`} />
                <div className={`text-2xl font-bold ${(profile?.dispute_count || 0) === 0 ? 'text-primary' : (profile?.dispute_count || 0) <= 2 ? 'text-yellow-500' : 'text-destructive'}`}>
                  {profile?.dispute_count || 0}
                </div>
                <div className="text-sm text-muted-foreground">Disputes</div>
              </CardContent>
            </Card>
          </div>

          {/* Review Scores */}
          {reviewAggregate && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Star className="h-4 w-4" />
                  Average Review Scores
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {formatScore(reviewAggregate.avg_quality_rating)}
                    </div>
                    <div className="text-sm text-muted-foreground">Quality</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {formatScore(reviewAggregate.avg_timeliness_rating)}
                    </div>
                    <div className="text-sm text-muted-foreground">Timeliness</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {formatScore(reviewAggregate.avg_communication_rating)}
                    </div>
                    <div className="text-sm text-muted-foreground">Communication</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Certifications */}
          {certifications && certifications.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Certifications
                </h3>
                <div className="flex flex-wrap gap-2">
                  {certifications.map((cert, index) => (
                    <Badge key={index} variant="secondary">
                      {cert.certification_type?.name || cert.certification_name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Capabilities */}
          {capabilities && capabilities.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Capabilities
                </h3>
                <div className="flex flex-wrap gap-2">
                  {capabilities.map((capability, index) => (
                    <Badge key={index} variant="outline">
                      {capability.capability?.name || 'Unknown'}
                      {capability.business_percentage > 0 && ` (${capability.business_percentage}%)`}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Gallery */}
          {profile?.gallery_images && profile.gallery_images.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3">Gallery</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {(profile.gallery_images as GalleryImage[]).map((image, index) => (
                    <div key={image.storage_key || index} className="aspect-square rounded-lg overflow-hidden border bg-muted cursor-pointer hover:opacity-90 transition-opacity">
                      <img
                        src={image.url}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Reviews */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Recent Reviews
                {reviews && reviews.length > 0 && (
                  <span className="text-sm font-normal text-muted-foreground">
                    ({reviews.length} total)
                  </span>
                )}
              </h3>
              
              {!reviews || reviews.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No reviews yet
                </p>
              ) : (
                <div className="space-y-3">
                  {displayedReviews?.map((review) => (
                    <ReviewCard 
                      key={review.id} 
                      review={review}
                      reviewerName="Client"
                    />
                  ))}
                  
                  {reviews.length > 3 && !showAllReviews && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowAllReviews(true)}
                      className="w-full"
                    >
                      Show all {reviews.length} reviews
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}